'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@/hooks/useTranslation';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { formatMeetingDateTimeEn } from '@/features/meeting-attendance/utils/date';
import { ArrowLeft, Download, RefreshCw, QrCode as QrCodeIcon } from 'lucide-react';
import { toast } from 'sonner';

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  location: string | null;
  qrCodeOnline: string | null;
  qrCodeOffline: string | null;
  status: string;
}

export default function MeetingAttendanceQrPage() {
  const params = useParams();
  const meetingId = params.id as string;
  const { t } = useTranslation();
  const qrTexts = t.meetingAttendance.qrPage;
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMeeting = useCallback(async () => {
    try {
      const data = await meetingAttendanceApi.getMeeting(meetingId);
      setMeeting(data);
    } catch (error) {
      console.error('Failed to fetch meeting info:', error);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    fetchMeeting();
  }, [fetchMeeting]);

  const handleDownload = async (type: 'online' | 'offline', useTemplate = false) => {
    const qrCode = type === 'online' ? meeting?.qrCodeOnline : meeting?.qrCodeOffline;
    if (!qrCode || !meeting) return;

    const THEME = {
      primary: type === 'online' ? '#2563eb' : '#16a34a',
      primaryDark: type === 'online' ? '#1e40af' : '#15803d',
      bg: '#ffffff',
      text: '#111827',
      sub: '#374151',
      mute: '#6b7280',
      hair: '#e5e7eb',
      card: '#f9fafb',
    };

    const BASE = { width: 2804, height: 1376, padding: 64, gutter: 48 };

    const createCanvas = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = BASE.width * dpr;
      canvas.height = BASE.height * dpr;
      canvas.style.width = `${BASE.width}px`;
      canvas.style.height = `${BASE.height}px`;
      ctx.scale(dpr, dpr);
      ctx.imageSmoothingQuality = 'high';
      return { canvas, ctx };
    };

    const setFont = (ctx: CanvasRenderingContext2D, size: number, weight: 'normal' | 'bold' = 'normal') => {
      ctx.font = `${weight} ${size}px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "PingFang SC", "Microsoft YaHei", sans-serif`;
    };

    const wrapText = (
      ctx: CanvasRenderingContext2D,
      text: string,
      x: number,
      y: number,
      maxWidth: number,
      lineHeight: number,
    ) => {
      const words = text.split(/\s+/);
      let line = '';
      let cursorY = y;
      for (let i = 0; i < words.length; i += 1) {
        const test = line ? `${line} ${words[i]}` : words[i];
        const width = ctx.measureText(test).width;
        if (width > maxWidth && i > 0) {
          ctx.fillText(line, x, cursorY);
          line = words[i];
          cursorY += lineHeight;
        } else {
          line = test;
        }
      }
      ctx.fillText(line, x, cursorY);
      return cursorY;
    };

    const drawRoundedRect = (
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      w: number,
      h: number,
      r: number,
    ) => {
      const rr = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.arcTo(x + w, y, x + w, y + h, rr);
      ctx.arcTo(x + w, y + h, x, y + h, rr);
      ctx.arcTo(x, y + h, x, y, rr);
      ctx.arcTo(x, y, x + w, y, rr);
      ctx.closePath();
    };

    const drawChip = (ctx: CanvasRenderingContext2D, label: string, x: number, y: number) => {
      setFont(ctx, 20, 'bold');
      const paddingX = 12;
      const paddingY = 8;
      const width = ctx.measureText(label).width + paddingX * 2;
      const height = 32 + paddingY * 2 - 24;
      drawRoundedRect(ctx, x, y, width, height, 8);
      ctx.fillStyle = THEME.card;
      ctx.fill();
      ctx.strokeStyle = THEME.primary;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = THEME.primaryDark;
      ctx.fillText(label, x + paddingX, y + height - paddingY - 4);
      return { width, height };
    };

    try {
      toast.loading(qrTexts.toastGenerating);

      const created = createCanvas();
      if (!created) {
        toast.error(qrTexts.toastCanvasFailed);
        return;
      }
      const { canvas, ctx } = created;

      const qrImage = new window.Image();
      qrImage.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        qrImage.onload = () => resolve();
        qrImage.onerror = () => reject(new Error('QR load failed'));
        qrImage.src = qrCode;
      }).catch(() => {
        toast.dismiss();
        toast.error(qrTexts.toastQrFailed);
        throw new Error('QR load failed');
      });

      if (useTemplate) {
        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
        const templateFileName = type === 'online' ? 'online-template.png' : 'onsite-template.png';
        const templatePath = `${basePath}/templates/${templateFileName}`;

        const templateImage = new window.Image();

        await new Promise<void>((resolve, reject) => {
          templateImage.onload = () => resolve();
          templateImage.onerror = () => reject(new Error('Template load failed'));
          templateImage.src = templatePath;
        }).catch(() => {
          toast.dismiss();
          toast.error(qrTexts.toastTemplateFailed);
          throw new Error('Template load failed');
        });

        ctx.drawImage(templateImage, 0, 0, BASE.width, BASE.height);

        const qrConfig = {
          size: 690,
          x: 360,
          y: 370,
        };

        ctx.drawImage(qrImage, qrConfig.x, qrConfig.y, qrConfig.size, qrConfig.size);

        canvas.toBlob((blob) => {
          toast.dismiss();
          if (!blob) {
            toast.error(qrTexts.toastGenerateFailed);
            return;
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${type === 'online' ? 'online' : 'onsite'}-${meeting.title}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });
        return;
      }

      const qrWidth = 520;
      const qrHeight = 520;
      const qrX = BASE.padding;
      const qrY = BASE.padding + 120;

      ctx.fillStyle = THEME.bg;
      ctx.fillRect(0, 0, BASE.width, BASE.height);

      setFont(ctx, 44, 'bold');
      ctx.fillStyle = THEME.text;
      ctx.fillText('Meeting Attendance', BASE.padding, BASE.padding + 44);

      setFont(ctx, 32, 'normal');
      ctx.fillStyle = THEME.sub;
      ctx.fillText(meeting.title, BASE.padding, BASE.padding + 92);

      drawChip(ctx, type === 'online' ? 'ONLINE' : 'ON-SITE', BASE.padding, BASE.padding + 110);

      ctx.drawImage(qrImage, qrX, qrY, qrWidth, qrHeight);

      setFont(ctx, 26, 'bold');
      ctx.fillStyle = THEME.text;
      ctx.fillText('Meeting Info', BASE.padding, qrY + qrHeight + 60);

      setFont(ctx, 22, 'normal');
      ctx.fillStyle = THEME.sub;
      ctx.fillText(`Start: ${formatMeetingDateTimeEn(meeting.startTime)}`, BASE.padding, qrY + qrHeight + 100);
      ctx.fillText(`End: ${formatMeetingDateTimeEn(meeting.endTime)}`, BASE.padding, qrY + qrHeight + 140);
      ctx.fillText(`Location: ${meeting.location || '-'}`, BASE.padding, qrY + qrHeight + 180);

      setFont(ctx, 22, 'normal');
      ctx.fillStyle = THEME.mute;
      wrapText(ctx, meeting.description || '', BASE.padding, qrY + qrHeight + 220, BASE.width - BASE.padding * 2, 30);

      canvas.toBlob((blob) => {
        toast.dismiss();
        if (!blob) {
          toast.error(qrTexts.toastGenerateFailed);
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type}-meeting-${meeting.id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Failed to generate QR code image:', error);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMeeting();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <p className="text-gray-600">{t.meetingAttendance.common.loading}</p>
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  const statusLabel = meeting.status === 'SCHEDULED'
    ? t.meetingAttendance.status.scheduled
    : meeting.status === 'IN_PROGRESS'
      ? t.meetingAttendance.status.inProgress
      : meeting.status === 'COMPLETED'
        ? t.meetingAttendance.status.completed
        : t.meetingAttendance.status.cancelled;
  const statusClass = meeting.status === 'SCHEDULED'
    ? 'bg-blue-100 text-blue-800'
    : meeting.status === 'IN_PROGRESS'
      ? 'bg-green-100 text-green-800'
      : meeting.status === 'COMPLETED'
        ? 'bg-gray-100 text-gray-800'
        : 'bg-red-100 text-red-800';

  const renderInstructions = (variant: 'online' | 'offline') => {
    const isOnline = variant === 'online';
    const subtitle = isOnline ? qrTexts.onlineInstructionsSubtitle : qrTexts.offlineInstructionsSubtitle;
    const tipsFirst = isOnline ? qrTexts.tipsOnline1 : qrTexts.tipsOffline1;
    const styles = isOnline
      ? {
          card: 'shadow-md border border-blue-200 bg-blue-50/30',
          stepBadge: 'bg-blue-100 text-blue-600',
          tipBox: 'bg-blue-50 border-blue-200',
          bullet: 'text-blue-600',
        }
      : {
          card: 'shadow-md border border-green-200 bg-green-50/30',
          stepBadge: 'bg-green-100 text-green-600',
          tipBox: 'bg-green-50 border-green-200',
          bullet: 'text-green-600',
        };

    return (
      <Card className={styles.card}>
        <CardHeader className="space-y-2 pb-6">
          <CardTitle className="text-xl font-bold text-gray-900">{qrTexts.instructionsTitle}</CardTitle>
          <CardDescription className="text-sm text-gray-600">{subtitle}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-5">
            {[1, 2, 3].map((step) => {
              const title = step === 1 ? qrTexts.step1Title : step === 2 ? qrTexts.step2Title : qrTexts.step3Title;
              const descEn = step === 1 ? qrTexts.step1DescEn : step === 2 ? qrTexts.step2DescEn : qrTexts.step3DescEn;
              const descZh = step === 1 ? qrTexts.step1DescZh : step === 2 ? qrTexts.step2DescZh : qrTexts.step3DescZh;

              return (
                <div key={title} className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-7 h-7 ${styles.stepBadge} rounded-full flex items-center justify-center text-sm font-bold`}>
                    {step}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">{title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{descEn}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{descZh}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`p-4 rounded-lg border ${styles.tipBox}`}>
            <h4 className="font-bold text-gray-900 mb-3 flex items-center text-sm">
              <span className="mr-2">💡</span>
              {qrTexts.tipsTitle}
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {[tipsFirst, qrTexts.tips2, qrTexts.tips3, qrTexts.tips4].map((tip) => (
                <li key={tip} className="flex items-start">
                  <span className={`${styles.bullet} mr-2`}>•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-4">
          <Link href={`/meetingattendance/meetings/${meetingId}`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {qrTexts.backToMeeting}
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{qrTexts.title}</h1>
            <p className="mt-1 text-sm text-gray-500">{meeting.title}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-md border border-blue-200 bg-blue-50/30">
          <CardHeader className="space-y-2 pb-6">
            <div className="flex items-center space-x-2">
              <QrCodeIcon className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-xl font-bold text-blue-600">{qrTexts.onlineTitle}</CardTitle>
            </div>
            <CardDescription className="text-sm text-gray-600 pl-7">{qrTexts.onlineSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-6">
            {meeting.qrCodeOnline ? (
              <div className="p-3 bg-white border-[3px] border-blue-500 rounded-2xl">
                <Image
                  src={meeting.qrCodeOnline}
                  alt={qrTexts.onlineTitle}
                  width={300}
                  height={300}
                  className="rounded-lg"
                />
              </div>
            ) : (
              <div className="w-[300px] h-[300px] border-4 border-dashed border-gray-300 rounded-2xl flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <QrCodeIcon className="h-16 w-16 mx-auto mb-3" />
                  <p className="font-medium">{qrTexts.onlineUnavailable}</p>
                </div>
              </div>
            )}

            <Button
              onClick={() => handleDownload('online', true)}
              disabled={!meeting.qrCodeOnline}
              className="w-full max-w-sm bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="mr-2 h-4 w-4" />
              {qrTexts.downloadImage}
            </Button>
          </CardContent>
        </Card>

        {renderInstructions('online')}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-md border border-green-200 bg-green-50/30">
          <CardHeader className="space-y-2 pb-6">
            <div className="flex items-center space-x-2">
              <QrCodeIcon className="h-5 w-5 text-green-600" />
              <CardTitle className="text-xl font-bold text-green-600">{qrTexts.offlineTitle}</CardTitle>
            </div>
            <CardDescription className="text-sm text-gray-600 pl-7">{qrTexts.offlineSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-8 space-y-6">
            {meeting.qrCodeOffline ? (
              <div className="p-3 bg-white border-[3px] border-green-500 rounded-2xl">
                <Image
                  src={meeting.qrCodeOffline}
                  alt={qrTexts.offlineTitle}
                  width={300}
                  height={300}
                  className="rounded-lg"
                />
              </div>
            ) : (
              <div className="w-[300px] h-[300px] border-4 border-dashed border-gray-300 rounded-2xl flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <QrCodeIcon className="h-16 w-16 mx-auto mb-3" />
                  <p className="font-medium">{qrTexts.offlineUnavailable}</p>
                </div>
              </div>
            )}

            <Button
              onClick={() => handleDownload('offline', true)}
              disabled={!meeting.qrCodeOffline}
              className="w-full max-w-sm bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="mr-2 h-4 w-4" />
              {qrTexts.downloadImage}
            </Button>
          </CardContent>
        </Card>

        {renderInstructions('offline')}
      </div>

      <div className="flex justify-center">
        <Button variant="outline" onClick={handleRefresh} size="lg">
          <RefreshCw className="mr-2 h-5 w-5" />
          {qrTexts.refresh}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{qrTexts.meetingInfoTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">{qrTexts.meetingInfoTitleLabel}</span>
              <span className="text-gray-900">{meeting.title}</span>
            </div>
            {meeting.description && (
              <div>
                <span className="font-medium text-gray-700">{qrTexts.meetingInfoDescriptionLabel}</span>
                <span className="text-gray-900">{meeting.description}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">{qrTexts.meetingInfoStartLabel}</span>
              <span className="text-gray-900">{formatMeetingDateTimeEn(meeting.startTime)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">{qrTexts.meetingInfoEndLabel}</span>
              <span className="text-gray-900">{formatMeetingDateTimeEn(meeting.endTime)}</span>
            </div>
            {meeting.location && (
              <div>
                <span className="font-medium text-gray-700">{qrTexts.meetingInfoLocationLabel}</span>
                <span className="text-gray-900">{meeting.location}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">{qrTexts.meetingInfoStatusLabel}</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                {statusLabel}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
